// AgencyCredentialing.js — the "Credentialing" tab of the agency portal.
//
// Rendered inside AgencyBoard at /agency/:token, so there is ONE link per
// agency covering both the staffing board and this. Nothing here is
// authenticated by a Supabase session: the token in the URL is the authority,
// and every endpoint re-derives the agency from it server-side.
//
// What this screen is for: the agency adds its own nurse, uploads the
// documents it already holds, and hands its nurse the signing link itself.
// A Heart For Care emails the nurse nothing — the request reaches her from
// her own employer, which is the point of the whole rework.
//
// The agency can also open and download every credential on file for its own
// people, whoever uploaded it (agreement §5). Which documents those are is
// decided server-side in _shared/agency-documents.ts; this screen only lists
// what the roster returns and asks get-agency-document for the file.
import { useCallback, useEffect, useRef, useState } from 'react';
import { shrinkForUpload } from '../lib/documents/shrinkImage';
import { portalFetch, isStaffView } from '../lib/agencyPortalClient';

const FN = (name) => `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/${name}`;

const post = async (name, body) => {
  const res = await fetch(FN(name), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
};

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(',')[1]);
  r.onerror = reject;
  r.readAsDataURL(file);
});

// Same three-part shape the careers form collects, so an agency submission and
// a careers application arrive in the pipeline looking identical.
const UPLOADS = [
  { key: 'resume_url', label: 'Résumé', hint: 'Must show experience with medically fragile patients (GAPP § 609.3)' },
  { key: 'id_document_url', label: 'Government photo ID', hint: "Driver's license or state ID" },
  { key: 'cpr_card_url', label: 'CPR / First Aid card', dateKey: 'cpr_expiry_date', dateLabel: 'Expiry date' },
  { key: 'tb_result_url', label: 'TB test result', dateKey: 'tb_test_date', dateLabel: 'Test date' },
  { key: 'license_cert_url', label: 'Nursing license / certificate', dateKey: 'license_expiry_date', dateLabel: 'Expiry date' },
];

const ROLES = [
  { value: 'rn', label: 'Registered Nurse (RN)' },
  { value: 'lpn', label: 'Licensed Practical Nurse (LPN)' },
  { value: 'pca', label: 'Personal Care Support / CNA' },
];

const STAGE_TONE = {
  documents: ['var(--warn-bg)', 'var(--warn)', 'var(--warn-border)'],
  ready_to_send: ['var(--info-bg)', 'var(--info)', 'var(--info-border)'],
  awaiting_signature: ['var(--info-bg)', 'var(--info)', 'var(--info-border)'],
  review: ['var(--bg2)', 'var(--text2)', 'var(--border)'],
  active: ['var(--success-bg)', 'var(--success)', 'var(--success)'],
  withdrawn: ['var(--bg2)', 'var(--text3)', 'var(--border)'],
};

const Badge = ({ stage, children }) => {
  const t = STAGE_TONE[stage] || STAGE_TONE.review;
  return (
    <span style={{
      display: 'inline-block', fontSize: 11, padding: '2px 8px', borderRadius: 4, whiteSpace: 'nowrap',
      background: t[0], color: t[1], border: `0.5px solid ${t[2]}`,
    }}>{children}</span>
  );
};

const Field = ({ label, hint, children, required }) => (
  <label style={{ display: 'block', marginBottom: 12 }}>
    <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>
      {label}{required && <span style={{ color: 'var(--danger)' }}> *</span>}
    </div>
    {children}
    {hint && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>{hint}</div>}
  </label>
);

const inputStyle = {
  width: '100%', padding: '7px 9px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit',
  border: '0.5px solid var(--border)', background: 'var(--card-bg)', color: 'var(--text)',
};

// ── Add-a-nurse form ────────────────────────────────────────────────────────
function AddNurseForm({ token, agencyName, onDone, onCancel }) {
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', date_of_birth: '',
    address: '', city: '', state: 'GA', zip: '', ssn: '',
    position_applied_for: '', work_history: '', submitted_by_name: '',
  });
  const [files, setFiles] = useState({});
  const [dates, setDates] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState('');

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);

    const need = [];
    if (!form.first_name.trim()) need.push('First name');
    if (!form.last_name.trim()) need.push('Last name');
    if (!form.email.trim()) need.push('Email');
    if (!form.phone.trim()) need.push('Phone');
    if (!form.date_of_birth) need.push('Date of birth');
    if (!form.position_applied_for) need.push('Role');
    if (!form.submitted_by_name.trim()) need.push('Your name');
    const ssnDigits = form.ssn.replace(/\D/g, '');
    if (ssnDigits && ssnDigits.length !== 9) need.push('Social Security number must be 9 digits');
    if (need.length) { setError(`Still needed: ${need.join(', ')}`); return; }

    setBusy(true);
    try {
      // Files go to Drive first; the submission carries the resulting links.
      // A failed upload aborts before any record is created, so a half-built
      // candidate never lands in the pipeline.
      const urls = {};
      const applicantName = `${form.first_name.trim()} ${form.last_name.trim()}`;
      for (const u of UPLOADS) {
        const file = files[u.key];
        if (!file) continue;
        setProgress(`Uploading ${u.label}…`);
        // A phone photo is shrunk first, as on the application form: at full
        // size over mobile data it can time out before it arrives.
        const sendable = await shrinkForUpload(file);
        const data = await post('upload-staff-document', {
          fileBase64: await fileToBase64(sendable),
          filename: sendable.name,
          applicantName,
          contentType: sendable.type || 'application/octet-stream',
        });
        if (!data?.success) throw new Error(data?.error || `Upload failed for ${u.label}`);
        urls[u.key] = data.url;
      }

      setProgress('Submitting…');
      const res = await post('submit-agency-staff', { token, ...form, ...urls, ...dates });
      if (!res?.success) {
        if (res?.error === 'duplicate') { setError(res.message); return; }
        if (res?.error === 'validation') { setError(`Still needed: ${(res.missing || []).join(', ')}`); return; }
        setError('Could not submit. Check your connection and try again, or call the office.');
        return;
      }
      onDone(res);
    } catch (err) {
      setError(err.message || 'Something went wrong. Nothing was saved — try again.');
    } finally {
      setBusy(false);
      setProgress('');
    }
  };

  return (
    <form onSubmit={submit} className="card" style={{ padding: 18, marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Add a nurse</div>
      <p style={{ fontSize: 12, color: 'var(--text3)', margin: '0 0 16px', lineHeight: 1.55, maxWidth: '70ch' }}>
        These are the details A Heart For Care must hold and, for several of them, send to the Georgia
        Department of Community Health before {form.first_name || 'your nurse'} can work a GAPP case.
        Upload what you already hold in her file — you don't need to collect anything new.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '0 14px' }}>
        <Field label="First name" required><input style={inputStyle} value={form.first_name} onChange={set('first_name')} /></Field>
        <Field label="Last name" required><input style={inputStyle} value={form.last_name} onChange={set('last_name')} /></Field>
        <Field label="Email" required hint="Where her signing link will work from"><input style={inputStyle} type="email" value={form.email} onChange={set('email')} /></Field>
        <Field label="Phone" required><input style={inputStyle} value={form.phone} onChange={set('phone')} /></Field>
        <Field label="Date of birth" required><input style={inputStyle} type="date" value={form.date_of_birth} onChange={set('date_of_birth')} /></Field>
        <Field label="Role" required>
          <select style={inputStyle} value={form.position_applied_for} onChange={set('position_applied_for')}>
            <option value="">Select…</option>
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </Field>
        <Field label="Street address"><input style={inputStyle} value={form.address} onChange={set('address')} /></Field>
        <Field label="City"><input style={inputStyle} value={form.city} onChange={set('city')} /></Field>
        <Field label="State"><input style={inputStyle} value={form.state} onChange={set('state')} /></Field>
        <Field label="ZIP"><input style={inputStyle} value={form.zip} onChange={set('zip')} /></Field>
        <Field label="Social Security number" hint="Stored encrypted; only the last 4 digits are ever displayed">
          <input style={inputStyle} inputMode="numeric" value={form.ssn} onChange={set('ssn')} placeholder="•••-••-••••" />
        </Field>
      </div>

      <Field label="Summary of nursing experience" hint="Optional if the résumé covers it — GAPP § 901 requires experience with medically fragile patients">
        <textarea style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} value={form.work_history} onChange={set('work_history')} />
      </Field>

      <div style={{ fontSize: 12, fontWeight: 600, margin: '10px 0 8px' }}>Documents</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0 14px' }}>
        {UPLOADS.map((u) => (
          <div key={u.key}>
            <Field label={u.label} hint={u.hint}>
              <input
                type="file" style={{ ...inputStyle, padding: 6 }}
                accept="application/pdf,image/*"
                onChange={(e) => setFiles((p) => ({ ...p, [u.key]: e.target.files?.[0] || null }))}
              />
            </Field>
            {u.dateKey && files[u.key] && (
              <Field label={u.dateLabel}>
                <input
                  style={inputStyle} type="date" value={dates[u.dateKey] || ''}
                  onChange={(e) => setDates((p) => ({ ...p, [u.dateKey]: e.target.value }))}
                />
              </Field>
            )}
          </div>
        ))}
      </div>

      <Field label="Your name" required hint={`Recorded as the ${agencyName} administrator who submitted this`}>
        <input style={inputStyle} value={form.submitted_by_name} onChange={set('submitted_by_name')} />
      </Field>

      {error && (
        <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', color: 'var(--danger)', borderRadius: 6, padding: '9px 11px', fontSize: 12.5, marginBottom: 12 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
          {busy ? (progress || 'Submitting…') : 'Submit to A Heart For Care'}
        </button>
        <button type="button" className="btn btn-outline btn-sm" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </form>
  );
}

// ── Signing link row ────────────────────────────────────────────────────────
// Shows the link as copyable text rather than sending it. AHFC deliberately
// emails the nurse nothing: the agency passes this on itself.
function SignLink({ person, token, onChanged }) {
  const [link, setLink] = useState(person.signLink ? `${window.location.origin}${person.signLink}` : null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const generate = async () => {
    setBusy(true); setError(null);
    const res = await post('agency-request-signature', { token, staffId: person.id });
    setBusy(false);
    if (!res?.success) {
      setError(res?.error === 'already_signed' ? 'Already signed.' : 'Could not create the link — try again.');
      return;
    }
    setLink(`${window.location.origin}${res.signLink}`);
    onChanged?.();
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      timer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Copy failed — select the link and copy it manually.');
    }
  };

  if (person.signedAt) {
    return <span style={{ fontSize: 11.5, color: 'var(--success)' }}>Signed {new Date(person.signedAt).toLocaleDateString()}</span>;
  }
  if (person.missing?.length) {
    return <span style={{ fontSize: 11.5, color: 'var(--text3)' }}>Upload the missing documents first</span>;
  }
  if (!link) {
    return (
      <>
        <button className="btn btn-primary btn-sm" onClick={generate} disabled={busy}>
          {busy ? 'Creating…' : 'Create signing link'}
        </button>
        {error && <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>{error}</div>}
      </>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <input
        readOnly value={link} onFocus={(e) => e.target.select()}
        style={{ ...inputStyle, width: 260, fontSize: 11, fontFamily: 'monospace', padding: '5px 7px' }}
      />
      <button className="btn btn-outline btn-sm" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
      {error && <div style={{ fontSize: 11, color: 'var(--danger)' }}>{error}</div>}
    </div>
  );
}

// ── Main ────────────────────────────────────────────────────────────────────
// get-agency-document answers with the file itself, or JSON when it refuses.
async function fetchDocument(access, body) {
  const res = await portalFetch(access, 'get-agency-document', body);
  if (!res.ok || (res.headers.get('content-type') || '').startsWith('application/json')) {
    throw new Error('unavailable');
  }
  const name = decodeURIComponent(res.headers.get('x-file-name') || 'document');
  return { blob: await res.blob(), name };
}

function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

const SOURCE_LABEL = {
  agency: 'Uploaded by your agency',
  nurse: 'Uploaded by the nurse',
  ahfc: 'Filed by A Heart For Care',
};

const fmtDate = (d) => (d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString() : null);

function docLine(doc) {
  const parts = [doc.group === 'signed' ? 'Signed by the nurse' : SOURCE_LABEL[doc.source]];
  if (doc.addedAt) parts.push(fmtDate(doc.addedAt));
  if (doc.expiry) parts.push(`${doc.expiryKind === 'due' ? 'due again' : 'expires'} ${doc.expiry}`);
  return parts.join(' · ');
}

const soon = (doc) => doc.expiry && new Date(`${doc.expiry}T00:00:00`) <= new Date(Date.now() + 60 * 86_400_000);

function DocumentPreview({ preview, onDownload, onClose }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!preview.blob) return undefined;
    const u = URL.createObjectURL(preview.blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [preview.blob]);

  const type = preview.blob?.type || '';
  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1100 }}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 860, height: '90vh', maxHeight: '90vh' }}>
        <div className="modal-header">
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{preview.doc.label}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>{preview.person}{preview.name ? ` · ${preview.name}` : ''}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-outline btn-sm" onClick={() => onDownload(preview.doc)}>Download</button>
            <button className="btn btn-outline btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, background: 'var(--bg2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {preview.error && <div style={{ fontSize: 13, color: 'var(--danger)', padding: 24 }}>Couldn’t open this document. Try again, or call the A Heart For Care office.</div>}
          {!preview.error && !url && <div className="loading-spinner" />}
          {url && type === 'application/pdf' && <iframe title={preview.doc.label} src={url} style={{ width: '100%', height: '100%', border: 0 }} />}
          {url && type.startsWith('image/') && <img src={url} alt={preview.doc.label} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}
          {url && type !== 'application/pdf' && !type.startsWith('image/') && (
            <div style={{ fontSize: 13, color: 'var(--text3)', padding: 24, textAlign: 'center' }}>
              This file can’t be shown here. Use Download to open it.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DocumentsPanel({ person, access, onClose }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const docs = person.documents || [];
  const groups = [
    ['Credentials', docs.filter((d) => d.group === 'credential')],
    ['Signed', docs.filter((d) => d.group === 'signed')],
  ].filter(([, list]) => list.length);

  const view = async (doc) => {
    setError(null);
    setPreview({ doc, person: person.name });
    try {
      const { blob, name } = await fetchDocument(access, { staffId: person.id, docId: doc.id, mode: 'view' });
      setPreview((p) => (p?.doc.id === doc.id ? { ...p, blob, name } : p));
    } catch {
      setPreview((p) => (p?.doc.id === doc.id ? { ...p, error: true } : p));
    }
  };

  const download = async (doc) => {
    setError(null);
    setBusy(doc ? doc.id : 'all');
    try {
      const { blob, name } = await fetchDocument(access, doc
        ? { staffId: person.id, docId: doc.id, mode: 'download' }
        : { staffId: person.id, mode: 'all' });
      saveBlob(blob, name);
    } catch {
      setError(doc ? `Couldn’t download ${doc.label}. Try again.` : 'Couldn’t prepare the download. Try again.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 620 }}>
        <div className="modal-header">
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{person.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
              {[person.role, person.stageLabel, `${docs.length} ${docs.length === 1 ? 'document' : 'documents'}`].filter(Boolean).join(' · ')}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {docs.length > 1 && (
              <button className="btn btn-outline btn-sm" onClick={() => download(null)} disabled={!!busy}>
                {busy === 'all' ? 'Preparing…' : 'Download all'}
              </button>
            )}
            <button className="btn btn-outline btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>
        <div className="modal-body">
          {error && <div style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 10 }}>{error}</div>}
          {!docs.length && (
            <div style={{ fontSize: 13, color: 'var(--text3)', padding: '12px 0' }}>
              Nothing on file for {person.name} yet.
            </div>
          )}
          {groups.map(([title, list]) => (
            <div key={title} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 5 }}>{title}</div>
              <div style={{ border: '0.5px solid var(--border)', borderRadius: 6 }}>
                {list.map((doc, i) => (
                  <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderTop: i ? '0.5px solid var(--border)' : 'none' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13 }}>{doc.label}</div>
                      <div style={{ fontSize: 11, color: soon(doc) ? 'var(--danger)' : 'var(--text3)' }}>{docLine(doc)}</div>
                    </div>
                    <button className="btn btn-outline btn-sm" onClick={() => view(doc)}>View</button>
                    <button className="btn btn-outline btn-sm" onClick={() => download(doc)} disabled={!!busy}>
                      {busy === doc.id ? '…' : 'Download'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <p style={{ fontSize: 11, color: 'var(--text3)', margin: '4px 0 0', lineHeight: 1.55 }}>
            Every view and download is logged against your agency’s link. Keep what you download as a confidential
            personnel record.
          </p>
        </div>
      </div>
      {preview && <DocumentPreview preview={preview} onDownload={download} onClose={() => setPreview(null)} />}
    </div>
  );
}

export default function AgencyCredentialing({ access, agencyName }) {
  const token = access?.token || null;
  // AHFC staff viewing from CAERA see what the agency sees but cannot act for
  // it: no submitting nurses, no signing links (those take the agency's link).
  const readOnly = isStaffView(access);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [adding, setAdding] = useState(false);
  const [flash, setFlash] = useState(null);
  const [openDocs, setOpenDocs] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await (await portalFetch(access, 'get-agency-roster')).json();
      if (!res?.success) { setStatus(res?.error || 'error'); return; }
      setData(res);
      setStatus('ok');
    } catch {
      setStatus('error');
    }
  }, [access]);

  useEffect(() => { load(); }, [load]);

  const roster = data?.roster || [];
  const needsAction = roster.filter((r) => ['documents', 'ready_to_send', 'awaiting_signature'].includes(r.stage));

  if (status === 'loading') {
    return <div className="empty-state" style={{ padding: '40px 0' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>;
  }
  if (status !== 'ok') {
    return <div style={{ padding: 24, fontSize: 13, color: 'var(--text3)' }}>Couldn’t load your roster. Refresh, or call the A Heart For Care office.</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Credentialing</div>
          <p style={{ fontSize: 12, color: 'var(--text3)', margin: '4px 0 0', lineHeight: 1.55, maxWidth: '78ch' }}>
            Add your nurses here and upload the documents you already hold for them. When a nurse’s file is
            complete, create her signing link and send it to her yourself — she signs one document, and it
            comes from you rather than from A Heart For Care.
          </p>
        </div>
        {!adding && !readOnly && <button className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>Add a nurse</button>}
      </div>

      {flash && (
        <div style={{ background: 'var(--success-bg)', border: '0.5px solid var(--success)', color: 'var(--success)', borderRadius: 6, padding: '9px 11px', fontSize: 12.5, marginBottom: 14 }}>
          {flash}
        </div>
      )}

      {adding && (
        <AddNurseForm
          token={token} agencyName={agencyName}
          onCancel={() => setAdding(false)}
          onDone={(res) => {
            setAdding(false);
            setFlash(`${res.name} submitted. Create her signing link below once you’re ready.`);
            load();
          }}
        />
      )}

      {needsAction.length > 0 && (
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>
          {needsAction.length} {needsAction.length === 1 ? 'nurse needs' : 'nurses need'} something from you.
        </div>
      )}

      <div className="card">
        <div className="table-wrapper" style={{ overflowX: 'auto' }}>
          <table style={{ minWidth: 960 }}>
            <thead><tr>
              <th style={{ color: 'var(--text3)' }}>Nurse</th>
              <th style={{ color: 'var(--text3)' }}>Role</th>
              <th style={{ color: 'var(--text3)' }}>Status</th>
              <th style={{ color: 'var(--text3)' }}>Outstanding</th>
              <th style={{ color: 'var(--text3)' }}>Documents</th>
              <th style={{ color: 'var(--text3)' }}>Signing link</th>
            </tr></thead>
            <tbody>
              {roster.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 28, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
                  No nurses submitted yet. Use “Add a nurse” to start.
                </td></tr>
              )}
              {roster.map((p) => (
                <tr key={p.id}>
                  <td>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{p.name}</div>
                    <div style={{ fontSize: 10.5, color: 'var(--text3)' }}>
                      Submitted {new Date(p.submittedAt).toLocaleDateString()}
                    </div>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text3)' }}>{p.role || '—'}</td>
                  <td><Badge stage={p.stage}>{p.stageLabel}</Badge></td>
                  <td style={{ fontSize: 11.5, color: p.missing.length ? 'var(--warn)' : 'var(--text3)', maxWidth: 230 }}>
                    {p.missing.length ? p.missing.join(', ') : '—'}
                    {p.expiring?.length > 0 && (
                      <div style={{ color: 'var(--danger)', marginTop: 3 }}>
                        {p.expiring.map((c) => `${c.label} ${c.expired ? 'expired' : 'expires'} ${c.expiry}`).join(' · ')}
                      </div>
                    )}
                  </td>
                  <td>
                    <button className="btn btn-outline btn-sm" onClick={() => setOpenDocs(p)}>
                      {p.documents?.length ? `${p.documents.length} ${p.documents.length === 1 ? 'file' : 'files'}` : 'None yet'}
                    </button>
                  </td>
                  <td>{readOnly ? <span style={{ fontSize: 11.5, color: 'var(--text3)' }}>{p.signLink ? 'Signing link created' : '—'}</span> : <SignLink person={p} token={token} onChanged={load} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p style={{ fontSize: 11, color: 'var(--text3)', marginTop: 16, lineHeight: 1.6, maxWidth: '78ch' }}>
        A Heart For Care verifies each nursing license at source with the Georgia Board of Nursing and runs
        the registry and exclusion checks itself — you don’t need to supply those. Licensed RNs and LPNs are
        exempt from GCHEXS fingerprinting under O.C.G.A. § 31-7-350 et seq.; CNAs and personal care support
        staff are not, so send their GCHEXS determination to the A Heart For Care office.
        Don’t forward this link — it identifies your agency and every use, including every document opened, is logged.
      </p>

      {openDocs && <DocumentsPanel person={openDocs} access={access} onClose={() => setOpenDocs(null)} />}
    </div>
  );
}
